/* ─────────────────────────────────────────────────────────────────────────────
 * section-fp-v2-reviews.js
 * Section "Avis clients" — Fiche Produit v2 (Figma 2026)
 * Widget custom branché sur l'API publique Yotpo :
 *   - liste + bottomline + filtres : api-cdn.yotpo.com/v3/storefront/…/reviews
 *     (endpoint du widget officiel). GET sans filtre, POST {"scores":[n]} avec
 *     filtre par note — même URL. `perPage` avis affichés, bouton "Voir plus"
 *     qui ajoute la fournée suivante au clic (pas d'autoload : le scroll de
 *     page reste maître). La réponse non filtrée porte aussi le bottomline
 *     (moyenne + distribution), d'où une seule requête au chargement.
 *   - traduction : le paramètre `lang` fait renvoyer par Yotpo les avis
 *     traduits par IA dans la locale active. `language` = langue servie,
 *     `translatedLanguage` = langue d'origine (absent si pas de traduction).
 *     L'ancien endpoint v1 `filter.json` ignore toute demande de traduction :
 *     il renvoyait les avis FR bruts sur la boutique US. Ne pas y revenir.
 *   - dépôt d'avis : POST api.yotpo.com/v1/widget/reviews (modération Yotpo)
 *   - diffusion : le bottomline non filtré est republié
 *     ('fpv2:reviews:bottomline' + window.fpv2ReviewsBottomline) pour que le
 *     bloc étoiles du haut de fiche affiche les mêmes chiffres que la liste.
 * Tout le HTML est construit via createElement/textContent (contenu utilisateur,
 * jamais d'innerHTML sur les données API).
 * ────────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var STAR_PATH = 'M12 3.1l2.53 5.53 6.04.69-4.48 4.11 1.21 5.96L12 16.4l-5.3 2.99 1.21-5.96-4.48-4.11 6.04-.69L12 3.1z';

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function svgStar(fillLayer) {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('focusable', 'false');
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', STAR_PATH);
    svg.appendChild(path);
    if (fillLayer) {
      var span = el('span', 'fp-v2-reviews__star-fill');
      span.appendChild(svg);
      return span;
    }
    return svg;
  }

  /* Clip à la SURFACE de l'étoile, pas à sa largeur (branches fines : un clip
     proportionnel fait paraître ¾ plein). Coupures mesurées sur le polygone,
     note arrondie au quart — même table que section-fp-v2-tokens.css. */
  function starFillPct(f) {
    if (f >= 1) return 100;
    if (f >= 0.75) return 60.54;
    if (f >= 0.5) return 50;
    if (f >= 0.25) return 39.46;
    return 0;
  }

  /* Rangée d'étoiles avec remplissage partiel (calque clippé en largeur). */
  function starRow(score, large) {
    var wrap = el('span', 'fp-v2-reviews__stars' + (large ? ' fp-v2-reviews__stars--lg' : ''));
    wrap.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < 5; i++) {
      var star = el('span', 'fp-v2-reviews__star');
      var base = svgStar(false);
      base.setAttribute('class', 'fp-v2-reviews__star-base');
      star.appendChild(base);
      var fill = svgStar(true);
      var pct = starFillPct(score - i);
      fill.style.width = pct + '%';
      star.appendChild(fill);
      wrap.appendChild(star);
    }
    return wrap;
  }

  /* Code ISO d'une langue → nom lisible dans la locale de la page
     ('fr' → "French" côté US). Repli sur le code si Intl ne suit pas. */
  function languageName(code) {
    try {
      var name = new Intl.DisplayNames([document.documentElement.lang || 'en'], { type: 'language' }).of(code);
      if (name && name !== code) return name;
    } catch (e) { /* Intl.DisplayNames indisponible */ }
    return String(code).toUpperCase();
  }

  /* Date d'un avis (`createdAt` Yotpo, UTC sans suffixe Z) → <time> au format
     numérique de la locale de la page (07/10/2026 en FR, 10/07/2026 en US). */
  function reviewDate(iso) {
    if (!iso) return null;
    var str = String(iso);
    var d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(str) ? str : str + 'Z');
    if (isNaN(d.getTime())) return null;
    var label;
    try {
      label = d.toLocaleDateString(document.documentElement.lang || 'en', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      });
    } catch (e) {
      label = d.toISOString().slice(0, 10);
    }
    var t = el('time', 'fp-v2-reviews__date', label);
    t.setAttribute('datetime', d.toISOString());
    return t;
  }

  function tpl(str, map) {
    var out = str;
    Object.keys(map).forEach(function (k) {
      out = out.split(k).join(String(map[k]));
    });
    return out;
  }

  function init(root) {
    var configEl = root.querySelector('[data-fpv2-rev-config]');
    if (!configEl) return;
    var cfg;
    try {
      cfg = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }
    // Mobile : 3 avis par fournée (affichage initial + chaque « Voir plus »),
    // quel que soit le réglage de section (qui reste la valeur desktop). Figé
    // au chargement : changer perPage en cours de route fausserait la
    // pagination Yotpo (page N × perPage).
    if (window.matchMedia('(max-width: 749px)').matches) {
      cfg.perPage = 3;
    }

    var S = cfg.strings || {};
    var listEl = root.querySelector('[data-fpv2-rev-list]');
    var avgEl = root.querySelector('[data-fpv2-rev-avg]');
    var countEl = root.querySelector('[data-fpv2-rev-count]');
    var summaryEl = root.querySelector('[data-fpv2-rev-summary]');
    var starsEl = root.querySelector('[data-fpv2-rev-stars]');
    var filtersToggle = root.querySelector('[data-fpv2-rev-filters-toggle]');
    var filtersPanel = root.querySelector('[data-fpv2-rev-filters]');
    var filtersPills = root.querySelector('[data-fpv2-rev-filters-pills]');
    var writeToggle = root.querySelector('[data-fpv2-rev-write-toggle]');
    var formWrap = root.querySelector('[data-fpv2-rev-form-wrap]');
    var form = root.querySelector('[data-fpv2-rev-form]');
    var formStatus = root.querySelector('[data-fpv2-rev-form-status]');
    var moreWrap = root.querySelector('[data-fpv2-rev-more]');
    var moreBtn = root.querySelector('[data-fpv2-rev-more-btn]');

    var state = {
      page: 0, /* dernière page chargée ; 0 = rien encore */
      pageCount: 1,
      star: null, /* null = toutes les notes */
      distribution: null,
      total: 0,
      loading: false,
      fetchSeq: 0
    };

    /* ── Bottomline (header) ─────────────────────────────────────────────── */
    /* Porté par la réponse non filtrée de la liste : pas de requête dédiée.
       Les réponses filtrées renvoient `bottomline: null` — on garde alors le
       header global déjà affiché. */
    function applyBottomline(bl) {
      if (!bl) return; /* header SSR (metafields) ou global déjà en place */
      state.distribution = bl.starDistribution || null;
      renderHeader(bl.averageScore, bl.totalReview);
      renderFilterPills(bl.totalReview);
      publishBottomline(bl.averageScore, bl.totalReview);
    }

    /* La liste fait foi pour toute la fiche : le bloc étoiles du haut
       (section-fp-v2-main.js) se recale sur ces chiffres, les metafields Yotpo
       n'y servent qu'au premier rendu. Mémorisé sur window en plus de
       l'événement, l'écouteur pouvant s'installer après la réponse. */
    function publishBottomline(avg, total) {
      var detail = {
        productId: String(cfg.productId),
        average: Number(avg) || 0,
        total: Number(total) || 0
      };
      window.fpv2ReviewsBottomline = window.fpv2ReviewsBottomline || {};
      window.fpv2ReviewsBottomline[detail.productId] = detail;
      document.dispatchEvent(new CustomEvent('fpv2:reviews:bottomline', { detail: detail }));
    }

    function renderHeader(avg, total) {
      if (!total) return;
      var lang = document.documentElement.lang || 'en';
      var display;
      try {
        display = Number(avg).toLocaleString(lang, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
      } catch (e) {
        display = (Math.round(avg * 10) / 10).toFixed(1);
      }
      if (avgEl) avgEl.textContent = display;
      if (countEl) countEl.textContent = '[' + tpl(S.basedOn || '[[c]]', { '[[c]]': total }) + ']';
      if (starsEl) {
        /* Quart d'étoile inférieur, comme le haut de fiche et les cards. */
        var fresh = starRow(Math.floor(Number(avg) * 4) / 4, true);
        fresh.setAttribute('data-fpv2-rev-stars', '');
        starsEl.replaceWith(fresh);
        starsEl = fresh;
      }
      if (summaryEl) summaryEl.classList.remove('is-empty');
    }

    /* ── Pills de filtre par note ────────────────────────────────────────── */
    function renderFilterPills(total) {
      if (!filtersPills) return;
      filtersPills.textContent = '';
      var mkPill = function (label, starVal, count) {
        var pill = el('button', 'fp-v2-reviews__pill', label + (count != null ? ' (' + count + ')' : ''));
        pill.type = 'button';
        pill.setAttribute('aria-pressed', String(state.star === starVal));
        if (count === 0) pill.disabled = true;
        pill.addEventListener('click', function () {
          if (state.star === starVal) return;
          state.star = starVal;
          filtersPills.querySelectorAll('.fp-v2-reviews__pill').forEach(function (p) {
            p.setAttribute('aria-pressed', 'false');
          });
          pill.setAttribute('aria-pressed', 'true');
          loadReviews('reset');
        });
        return pill;
      };
      filtersPills.appendChild(mkPill(S.allRatings || 'All', null, total));
      for (var star = 5; star >= 1; star--) {
        var count = state.distribution ? (state.distribution[String(star)] || 0) : null;
        filtersPills.appendChild(mkPill(tpl(S.starFilter || '[[c]] ★', { '[[c]]': star }), star, count));
      }
    }

    /* ── Liste des avis ──────────────────────────────────────────────────── */
    /* mode : 'reset' (1er chargement / nouveau filtre → remplace) ou 'append'
       (bouton "Voir plus" → ajoute la fournée suivante). */
    function loadReviews(mode) {
      if (state.loading) return;
      var append = mode === 'append';
      if (append && state.page >= state.pageCount) return;
      var page = append ? state.page + 1 : 1;
      var seq = ++state.fetchSeq;
      state.loading = true;
      listEl.setAttribute('aria-busy', 'true');
      if (append) setMoreLoading(true);
      else listEl.classList.add('is-loading');

      /* `lang` déclenche la traduction IA côté Yotpo ; sans lui l'API renvoie
         les avis dans leur langue d'origine. */
      var url = 'https://api-cdn.yotpo.com/v3/storefront/store/' + encodeURIComponent(cfg.appKey) +
        '/product/' + encodeURIComponent(String(cfg.productId)) +
        '/reviews?page=' + page + '&perPage=' + cfg.perPage + '&sort=date' +
        (cfg.lang ? '&lang=' + encodeURIComponent(cfg.lang) : '');

      /* Même URL pour les deux modes : GET tout, POST filtré par note. */
      var opts = state.star
        ? {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ scores: [state.star] })
          }
        : undefined;

      fetch(url, opts)
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          if (seq !== state.fetchSeq) return; /* réponse obsolète (spam clic) */
          var resp = data || {};
          var reviews = resp.reviews || [];
          var pagination = resp.pagination || {};
          state.total = pagination.total || 0;
          state.pageCount = Math.max(1, Math.ceil(state.total / cfg.perPage));
          state.page = page;
          /* Inutile de rejouer header + pills sur "Voir plus" : rien ne bouge. */
          if (!append) applyBottomline(resp.bottomline);
          renderList(reviews, append);
          updateShowMore();
        })
        .catch(function () {
          if (seq !== state.fetchSeq) return;
          if (append) {
            state.page = page - 1; /* la fournée a échoué : le bouton la rejouera */
            updateShowMore();
          } else {
            renderError();
            if (moreWrap) moreWrap.hidden = true; /* pas de "Voir plus" sans liste */
          }
        })
        .finally(function () {
          if (seq !== state.fetchSeq) return;
          state.loading = false;
          setMoreLoading(false);
          listEl.classList.remove('is-loading');
          listEl.setAttribute('aria-busy', 'false');
        });
    }

    /* Affiche / masque le bouton "Voir plus" selon qu'il reste des pages. */
    function updateShowMore() {
      if (!moreWrap) return;
      moreWrap.hidden = state.page >= state.pageCount;
    }

    function setMoreLoading(on) {
      if (!moreBtn) return;
      moreBtn.disabled = on;
      moreBtn.textContent = on ? (S.loadingMore || '…') : (S.showMore || 'Show more');
    }

    if (moreBtn) {
      moreBtn.addEventListener('click', function () { loadReviews('append'); });
    }

    function renderList(reviews, append) {
      if (!append) listEl.textContent = '';
      if (!reviews.length) {
        if (!append) listEl.appendChild(el('p', 'fp-v2-reviews__notice', S.empty || 'No reviews yet.'));
        return;
      }
      var pendingClamp = [];
      reviews.forEach(function (review) {
        var row = el('article', 'fp-v2-reviews__row');

        var who = el('div', 'fp-v2-reviews__who');
        var name = (review.user && review.user.displayName) || '—';
        who.appendChild(el('span', 'fp-v2-reviews__name', name));
        if (review.verifiedBuyer) {
          var badge = el('span', 'fp-v2-reviews__verified');
          var check = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          check.setAttribute('viewBox', '0 0 24 24');
          check.setAttribute('aria-hidden', 'true');
          check.setAttribute('focusable', 'false');
          var checkPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          checkPath.setAttribute('d', 'M23 12l-2.44-2.79.34-3.69-3.61-.82-1.89-3.2L12 2.96 8.6 1.5 6.71 4.69 3.1 5.5l.34 3.7L1 12l2.44 2.79-.34 3.7 3.61.82L8.6 22.5l3.4-1.47 3.4 1.46 1.89-3.19 3.61-.82-.34-3.69L23 12zm-12.91 4.72l-3.8-3.81 1.48-1.48 2.32 2.33 5.85-5.87 1.48 1.48-7.33 7.35z');
          check.appendChild(checkPath);
          badge.appendChild(check);
          badge.appendChild(document.createTextNode(S.verified || 'Verified Buyer'));
          who.appendChild(badge);
        }
        var dateEl = reviewDate(review.createdAt);
        if (dateEl) who.appendChild(dateEl);
        row.appendChild(who);

        var bodyCol = el('div', 'fp-v2-reviews__body');
        var head = el('div', 'fp-v2-reviews__row-head');
        head.appendChild(starRow(review.score || 0, false));
        if (review.title) head.appendChild(el('h3', 'fp-v2-reviews__row-title', review.title));
        bodyCol.appendChild(head);
        if (review.content) {
          var textEl = el('p', 'fp-v2-reviews__row-text', review.content);
          bodyCol.appendChild(textEl);
          pendingClamp.push(textEl);
        }
        /* `translatedLanguage` n'est renseigné que si Yotpo a traduit l'avis :
           on le signale, comme le widget natif. */
        if (review.translatedLanguage && review.translatedLanguage !== review.language) {
          var note = el('p', 'fp-v2-reviews__translated',
            tpl(S.translatedFrom || 'Translated from [[l]]', {
              '[[l]]': languageName(review.translatedLanguage)
            }));
          bodyCol.appendChild(note);
        }
        if (review.comment && review.comment.content) {
          var reply = el('div', 'fp-v2-reviews__reply');
          reply.appendChild(el('span', 'fp-v2-reviews__reply-label', S.replyFrom || 'Reply'));
          reply.appendChild(document.createTextNode(review.comment.content));
          bodyCol.appendChild(reply);
        }
        row.appendChild(bodyCol);
        listEl.appendChild(row);
      });
      /* Le clamp se mesure après layout (scrollHeight/clientHeight fiables). */
      requestAnimationFrame(function () { pendingClamp.forEach(setupClamp); });
    }

    /* Tronque un avis trop long à N lignes (CSS) et pose un bouton "Lire plus"
       seulement s'il déborde vraiment de la zone tronquée. */
    function setupClamp(textEl) {
      textEl.classList.add('is-clamped');
      if (textEl.scrollHeight - textEl.clientHeight <= 4) {
        textEl.classList.remove('is-clamped'); /* tient déjà : pas de bouton */
        return;
      }
      var btn = el('button', 'fp-v2-reviews__readmore', S.readMore || 'Read more');
      btn.type = 'button';
      btn.setAttribute('aria-expanded', 'false');
      btn.addEventListener('click', function () {
        var clamped = textEl.classList.toggle('is-clamped');
        btn.textContent = clamped ? (S.readMore || 'Read more') : (S.readLess || 'Read less');
        btn.setAttribute('aria-expanded', String(!clamped));
      });
      textEl.after(btn);
    }

    function renderError() {
      listEl.textContent = '';
      var notice = el('div', 'fp-v2-reviews__notice');
      notice.appendChild(el('p', null, S.error || 'Could not load reviews.'));
      var retry = el('button', 'fp-v2-reviews__btn', S.retry || 'Retry');
      retry.type = 'button';
      retry.addEventListener('click', function () { loadReviews('reset'); });
      notice.appendChild(retry);
      listEl.appendChild(notice);
    }

    /* ── Toggles panneaux (filtres / formulaire) ─────────────────────────── */
    function bindToggle(btn, panel, other) {
      if (!btn || !panel) return;
      btn.addEventListener('click', function () {
        var open = panel.hidden;
        panel.hidden = !open;
        btn.setAttribute('aria-expanded', String(open));
        if (open && other && !other.panel.hidden) {
          other.panel.hidden = true;
          other.btn.setAttribute('aria-expanded', 'false');
        }
      });
    }
    var filtersPair = { btn: filtersToggle, panel: filtersPanel };
    var writePair = { btn: writeToggle, panel: formWrap };
    bindToggle(filtersToggle, filtersPanel, writePair);
    bindToggle(writeToggle, formWrap, filtersPair);

    /* ── Formulaire ──────────────────────────────────────────────────────── */
    if (form) {
      var starInputs = form.querySelectorAll('.fp-v2-reviews__form-star-input');
      var starLabels = form.querySelectorAll('.fp-v2-reviews__form-star');
      function paintFormStars() {
        var score = 5;
        starInputs.forEach(function (input) {
          if (input.checked) score = parseInt(input.value, 10);
        });
        starLabels.forEach(function (label, idx) {
          label.classList.toggle('is-filled', idx < score);
        });
      }
      starInputs.forEach(function (input) {
        input.addEventListener('change', paintFormStars);
      });
      paintFormStars();

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var fd = new FormData(form);
        var payload = {
          appkey: cfg.appKey,
          sku: String(cfg.productId),
          product_title: cfg.productTitle,
          product_url: cfg.productUrl,
          display_name: (fd.get('display_name') || '').toString().trim(),
          email: (fd.get('email') || '').toString().trim(),
          review_score: parseInt(fd.get('review_score'), 10) || 5,
          review_title: (fd.get('review_title') || '').toString().trim(),
          review_content: (fd.get('review_content') || '').toString().trim()
        };
        if (!payload.display_name || !payload.email || !payload.review_title || !payload.review_content) {
          formStatus.textContent = S.formMissing || 'Please fill in all fields.';
          formStatus.classList.add('is-error');
          return;
        }
        var submitBtn = form.querySelector('.fp-v2-reviews__btn--submit');
        submitBtn.disabled = true;
        formStatus.classList.remove('is-error');
        formStatus.textContent = S.formSending || '…';

        fetch('https://api.yotpo.com/v1/widget/reviews', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        })
          .then(function (r) {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.json();
          })
          .then(function () {
            formStatus.textContent = S.formSuccess || 'Thank you!';
            form.reset();
            paintFormStars();
          })
          .catch(function () {
            formStatus.textContent = S.formError || 'Something went wrong.';
            formStatus.classList.add('is-error');
          })
          .finally(function () {
            submitBtn.disabled = false;
          });
      });

      var cancelBtn = form.querySelector('[data-fpv2-rev-form-cancel]');
      if (cancelBtn) {
        cancelBtn.addEventListener('click', function () {
          formWrap.hidden = true;
          writeToggle.setAttribute('aria-expanded', 'false');
        });
      }
    }

    /* ── Boot ────────────────────────────────────────────────────────────── */
    /* Une seule requête : la réponse non filtrée porte liste + bottomline. */
    loadReviews('reset');
  }

  function boot() {
    document.querySelectorAll('[data-section-type="fp-v2-reviews"]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor : ré-init quand la section est rechargée. */
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-section-type="fp-v2-reviews"]');
    if (root) init(root);
  });
})();
